import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../../contexts/AuthContext';
import { Employee } from '../../types/backend.types';
import api from '../../api/auth.api';
import { API_CONFIG } from '../../config/api.config';
import { CustomSelect } from '../common/CustomSelect';
import { Search, Users, UserPlus, Edit, Trash2, Key, Phone, UserX, Save, X, ChevronDown, ChevronRight } from 'lucide-react';

interface EmployeeWithForeman extends Employee {
  foreman_first_name?: string;
  foreman_last_name?: string;
}

interface CrewGroup {
  foreman: EmployeeWithForeman | null;
  members: EmployeeWithForeman[];
}

export const EmployeeManagement: React.FC = () => {
  const auth = useContext(AuthContext);

  // Admin-only access control
  if (!auth?.user) {
    return (
      <div style={{ padding: '20px', textAlign: 'center' }}>
        <h2>Access Denied</h2>
        <p>Please log in to access this page.</p>
      </div>
    );
  }

  if (auth.user.role !== 'admin') {
    return (
      <div style={{ padding: '20px', textAlign: 'center' }}>
        <h2>Access Denied</h2>
        <p>This page is only accessible to administrators.</p>
        <p>Your current role: <strong>{auth.user.role}</strong></p>
      </div>
    );
  }

  const [employees, setEmployees] = useState<EmployeeWithForeman[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState<'list' | 'crews'>('list');
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingEmployee, setEditingEmployee] = useState<EmployeeWithForeman | null>(null);
  const [showPinModal, setShowPinModal] = useState(false);
  const [pinEmployee, setPinEmployee] = useState<EmployeeWithForeman | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expandedCrews, setExpandedCrews] = useState<Set<number>>(new Set());

  // Form data for add/edit
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    nickname: '',
    email: '',
    phone: '',
    pin: '',
    role: 'field_worker' as 'admin' | 'foreman' | 'asphalt_foreman' | 'field_worker' | 'mechanic' | 'cdl_driver' | 'truck_driver',
    crew_foreman_id: ''
  });

  const [pinData, setPinData] = useState({ pin: '' });

  // Phone number formatter
  const formatPhoneNumber = (value: string) => {
    // Remove all non-digit characters
    const phoneNumber = value.replace(/\D/g, '');

    // Don't format if empty
    if (phoneNumber.length === 0) return '';

    // Format based on length
    if (phoneNumber.length <= 3) {
      return phoneNumber;
    } else if (phoneNumber.length <= 6) {
      return `${phoneNumber.slice(0, 3)}-${phoneNumber.slice(3)}`;
    } else if (phoneNumber.length <= 10) {
      return `${phoneNumber.slice(0, 3)}-${phoneNumber.slice(3, 6)}-${phoneNumber.slice(6, 10)}`;
    } else {
      // Limit to 10 digits
      return `${phoneNumber.slice(0, 3)}-${phoneNumber.slice(3, 6)}-${phoneNumber.slice(6, 10)}`;
    }
  };

  const handlePhoneChange = (value: string) => {
    const formatted = formatPhoneNumber(value);
    setFormData({ ...formData, phone: formatted });
  };

  // Load employees
  useEffect(() => {
    loadEmployees();
  }, []);

  const loadEmployees = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await api.get(`${API_CONFIG.ENDPOINTS.EMPLOYEES}?is_active=true`);
      if (response.data.success) {
        setEmployees(response.data.data);
      }
    } catch (err) {
      console.error('Failed to load employees:', err);
      setError('Failed to load employees');
    } finally {
      setLoading(false);
    }
  };

  // Filter employees
  const filteredEmployees = employees.filter(emp =>
    emp.first_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    emp.last_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (emp.nickname && emp.nickname.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  // Group employees by crew
  const crewGroups = (): CrewGroup[] => {
    const foremen = filteredEmployees.filter(emp => emp.role === 'foreman' || emp.role === 'asphalt_foreman');
    const groups: CrewGroup[] = [];

    // Add crews with foremen
    foremen.forEach(foreman => {
      const members = filteredEmployees.filter(emp => emp.crew_foreman_id === foreman.employee_id);
      groups.push({ foreman, members });
    });

    // Add unassigned employees
    const unassigned = filteredEmployees.filter(emp =>
      emp.role !== 'foreman' && emp.role !== 'asphalt_foreman' &&
      (emp.crew_foreman_id === null || emp.crew_foreman_id === undefined ||
       !foremen.some(f => f.employee_id === emp.crew_foreman_id))
    );

    if (unassigned.length > 0) {
      groups.push({ foreman: null, members: unassigned });
    }

    return groups;
  };

  // Toggle crew expansion
  const toggleCrewExpansion = (foremanId: number | null) => {
    const newExpanded = new Set(expandedCrews);
    const id = foremanId || 0; // Use 0 for unassigned
    if (newExpanded.has(id)) {
      newExpanded.delete(id);
    } else {
      newExpanded.add(id);
    }
    setExpandedCrews(newExpanded);
  };

  // Handle add employee
  const handleAddEmployee = () => {
    setEditingEmployee(null);
    setFormData({
      first_name: '',
      last_name: '',
      nickname: '',
      email: '',
      phone: '',
      pin: '',
      role: 'field_worker',
      crew_foreman_id: ''
    });
    setShowAddModal(true);
  };

  // Handle edit employee
  const handleEditEmployee = (employee: EmployeeWithForeman) => {
    setEditingEmployee(employee);
    setFormData({
      first_name: employee.first_name,
      last_name: employee.last_name,
      nickname: employee.nickname || '',
      email: employee.email || '', // Handle null email
      phone: formatPhoneNumber(employee.phone), // Format existing phone number
      pin: '', // Don't pre-fill PIN for security
      role: employee.role,
      crew_foreman_id: employee.crew_foreman_id?.toString() || ''
    });
    setShowAddModal(true);
  };

  // Handle change PIN
  const handleChangePinClick = (employee: EmployeeWithForeman) => {
    setPinEmployee(employee);
    setPinData({ pin: '' });
    setShowPinModal(true);
  };

  // Save employee (add or edit)
  const handleSaveEmployee = async () => {
    try {
      setLoading(true);

      // Validate required fields
      if (!formData.first_name || !formData.last_name || !formData.phone) {
        setError('Please fill in all required fields: First Name, Last Name, and Phone');
        return;
      }

      if (!editingEmployee && !formData.pin) {
        setError('PIN is required for new employees');
        return;
      }

      // Auto-clear crew_foreman_id if role is not field_worker
      const crewForemanId = (formData.role !== 'field_worker') ? null :
        (formData.crew_foreman_id ? parseInt(formData.crew_foreman_id) : null);

      const payload = {
        ...formData,
        email: formData.email?.trim() || null, // Set null if no email provided (allows multiple empty emails in DB)
        crew_foreman_id: crewForemanId
      };

      if (editingEmployee) {
        // Update existing employee (without PIN)
        const { pin, ...updatePayload } = payload;
        await api.put(`${API_CONFIG.ENDPOINTS.EMPLOYEES}/${editingEmployee.employee_id}`, updatePayload);
      } else {
        // Create new employee
        await api.post(API_CONFIG.ENDPOINTS.EMPLOYEES, payload);
      }

      setShowAddModal(false);
      loadEmployees();
    } catch (err: any) {
      console.error('Failed to save employee:', err);
      console.error('Error response:', err.response?.data);
      console.error('Error status:', err.response?.status);
      const errorMessage = err.response?.data?.error || err.message || 'Failed to save employee';
      setError(`Error: ${errorMessage} (Status: ${err.response?.status || 'unknown'})`);
    } finally {
      setLoading(false);
    }
  };

  // Update PIN
  const handleUpdatePin = async () => {
    if (!pinEmployee) return;

    try {
      setLoading(true);
      await api.put(`${API_CONFIG.ENDPOINTS.EMPLOYEES}/${pinEmployee.employee_id}/pin`, pinData);
      setShowPinModal(false);
      setPinEmployee(null);
    } catch (err: any) {
      console.error('Failed to update PIN:', err);
      setError(err.response?.data?.error || 'Failed to update PIN');
    } finally {
      setLoading(false);
    }
  };

  // Delete employee
  const handleDeleteEmployee = async (employee: EmployeeWithForeman) => {
    if (!confirm(`Are you sure you want to delete ${employee.first_name} ${employee.last_name}? This action cannot be undone.`)) {
      return;
    }

    try {
      setLoading(true);
      await api.delete(`${API_CONFIG.ENDPOINTS.EMPLOYEES}/${employee.employee_id}`);
      loadEmployees();
    } catch (err: any) {
      console.error('Failed to delete employee:', err);
      setError(err.response?.data?.error || 'Failed to delete employee');
    } finally {
      setLoading(false);
    }
  };

  // Deactivate employee
  const handleDeactivateEmployee = async (employee: EmployeeWithForeman) => {
    if (!confirm(`Are you sure you want to deactivate ${employee.first_name} ${employee.last_name}?`)) {
      return;
    }

    try {
      setLoading(true);
      await api.post(`${API_CONFIG.ENDPOINTS.EMPLOYEES}/${employee.employee_id}/deactivate`);
      loadEmployees();
    } catch (err: any) {
      console.error('Failed to deactivate employee:', err);
      setError(err.response?.data?.error || 'Failed to deactivate employee');
    } finally {
      setLoading(false);
    }
  };

  // Get foremen for dropdown
  const foremen = employees.filter(emp => emp.role === 'foreman' || emp.role === 'asphalt_foreman');

  const styles = {
    container: {
      padding: '20px',
      maxWidth: '1200px',
      margin: '0 auto',
      fontFamily: 'system-ui, -apple-system, sans-serif'
    },
    header: {
      marginBottom: '24px'
    },
    title: {
      fontSize: '24px',
      fontWeight: 'bold',
      color: '#1f2937',
      marginBottom: '8px'
    },
    subtitle: {
      color: '#6b7280',
      fontSize: '14px'
    },
    controls: {
      display: 'flex',
      flexDirection: 'column' as const,
      gap: '16px',
      marginBottom: '24px'
    },
    searchSection: {
      backgroundColor: 'white',
      border: '1px solid #e5e7eb',
      borderRadius: '8px',
      padding: '16px'
    },
    searchInput: {
      width: '100%',
      padding: '12px',
      border: '1px solid #d1d5db',
      borderRadius: '6px',
      fontSize: '16px',
      marginBottom: '12px'
    },
    viewToggle: {
      display: 'flex',
      gap: '8px',
      marginBottom: '16px'
    },
    toggleButton: {
      padding: '10px 16px',
      border: '1px solid #d1d5db',
      borderRadius: '6px',
      backgroundColor: 'white',
      color: '#6b7280',
      cursor: 'pointer',
      fontSize: '14px',
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      transition: 'all 0.2s'
    },
    toggleButtonActive: {
      backgroundColor: '#3b82f6',
      color: 'white',
      borderColor: '#3b82f6'
    },
    addButton: {
      padding: '12px 16px',
      backgroundColor: '#10b981',
      color: 'white',
      border: 'none',
      borderRadius: '6px',
      cursor: 'pointer',
      fontSize: '16px',
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      fontWeight: '500'
    },
    employeeList: {
      backgroundColor: 'white',
      border: '1px solid #e5e7eb',
      borderRadius: '8px',
      overflow: 'hidden'
    },
    employeeCard: {
      padding: '16px',
      borderBottom: '1px solid #f3f4f6',
      display: 'flex',
      flexDirection: 'column' as const,
      gap: '12px'
    },
    employeeHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'flex-start',
      gap: '12px'
    },
    employeeInfo: {
      flex: 1
    },
    employeeName: {
      fontSize: '18px',
      fontWeight: '600',
      color: '#1f2937',
      marginBottom: '4px'
    },
    employeeNickname: {
      fontSize: '16px',
      color: '#6b7280',
      fontStyle: 'italic',
      marginBottom: '8px'
    },
    employeeDetails: {
      display: 'flex',
      flexDirection: 'column' as const,
      gap: '4px'
    },
    detailRow: {
      fontSize: '14px',
      color: '#4b5563'
    },
    roleTag: {
      display: 'inline-block',
      padding: '4px 8px',
      borderRadius: '4px',
      fontSize: '12px',
      fontWeight: '600',
      textTransform: 'uppercase' as const
    },
    adminRole: {
      backgroundColor: '#fef3c7',
      color: '#92400e'
    },
    foremanRole: {
      backgroundColor: '#dbeafe',
      color: '#1e40af'
    },
    workerRole: {
      backgroundColor: '#f3f4f6',
      color: '#374151'
    },
    employeeActions: {
      display: 'flex',
      flexDirection: 'column' as const,
      gap: '8px',
      minWidth: '120px'
    },
    actionButton: {
      padding: '8px 12px',
      border: '1px solid #d1d5db',
      borderRadius: '4px',
      backgroundColor: 'white',
      cursor: 'pointer',
      fontSize: '14px',
      display: 'flex',
      alignItems: 'center',
      gap: '6px',
      justifyContent: 'center',
      transition: 'all 0.2s'
    },
    editButton: {
      color: '#3b82f6',
      borderColor: '#3b82f6'
    },
    pinButton: {
      color: '#f59e0b',
      borderColor: '#f59e0b'
    },
    deleteButton: {
      color: '#dc2626',
      borderColor: '#dc2626'
    },
    deactivateButton: {
      color: '#6b7280',
      borderColor: '#6b7280'
    },
    // Crew view styles
    crewSection: {
      marginBottom: '24px'
    },
    crewHeader: {
      backgroundColor: '#f9fafb',
      padding: '16px',
      borderRadius: '8px 8px 0 0',
      border: '1px solid #e5e7eb',
      borderBottom: 'none',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      cursor: 'pointer'
    },
    crewTitle: {
      fontSize: '18px',
      fontWeight: '600',
      color: '#1f2937',
      display: 'flex',
      alignItems: 'center',
      gap: '8px'
    },
    crewCount: {
      fontSize: '14px',
      color: '#6b7280'
    },
    crewMembers: {
      border: '1px solid #e5e7eb',
      borderTop: 'none',
      borderRadius: '0 0 8px 8px',
      overflow: 'hidden'
    },
    // Modal styles
    modal: {
      position: 'fixed' as const,
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: '20px'
    },
    modalContent: {
      backgroundColor: 'white',
      borderRadius: '8px',
      padding: '24px',
      width: '100%',
      maxWidth: '500px',
      maxHeight: '90vh',
      overflowY: 'auto' as const
    },
    modalHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '20px',
      paddingBottom: '12px',
      borderBottom: '1px solid #e5e7eb'
    },
    modalTitle: {
      fontSize: '18px',
      fontWeight: '600',
      color: '#1f2937'
    },
    closeButton: {
      padding: '4px',
      border: 'none',
      backgroundColor: 'transparent',
      cursor: 'pointer',
      color: '#6b7280'
    },
    formGroup: {
      marginBottom: '16px'
    },
    label: {
      display: 'block',
      fontSize: '14px',
      fontWeight: '500',
      color: '#374151',
      marginBottom: '6px'
    },
    input: {
      width: '100%',
      padding: '10px 12px',
      border: '1px solid #d1d5db',
      borderRadius: '6px',
      fontSize: '16px'
    },
    select: {
      width: '100%',
      padding: '10px 12px',
      border: '1px solid #d1d5db',
      borderRadius: '6px',
      fontSize: '16px',
      backgroundColor: 'white'
    },
    modalActions: {
      display: 'flex',
      gap: '12px',
      justifyContent: 'flex-end',
      marginTop: '24px',
      paddingTop: '12px',
      borderTop: '1px solid #e5e7eb'
    },
    saveButton: {
      padding: '10px 20px',
      backgroundColor: '#3b82f6',
      color: 'white',
      border: 'none',
      borderRadius: '6px',
      cursor: 'pointer',
      fontSize: '16px',
      display: 'flex',
      alignItems: 'center',
      gap: '8px'
    },
    cancelButton: {
      padding: '10px 20px',
      backgroundColor: '#6b7280',
      color: 'white',
      border: 'none',
      borderRadius: '6px',
      cursor: 'pointer',
      fontSize: '16px'
    },
    loading: {
      textAlign: 'center' as const,
      padding: '40px',
      color: '#6b7280'
    },
    error: {
      backgroundColor: '#fef2f2',
      border: '1px solid #fecaca',
      color: '#dc2626',
      padding: '12px',
      borderRadius: '6px',
      marginBottom: '16px'
    },
    noData: {
      textAlign: 'center' as const,
      padding: '40px',
      color: '#6b7280',
      fontStyle: 'italic'
    }
  };

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h1 style={styles.title}>Employee Management</h1>
        <p style={styles.subtitle}>Manage employee accounts, roles, and crew assignments</p>
      </div>

      {error && (
        <div style={styles.error}>
          {error}
          <button
            onClick={() => setError(null)}
            style={{ float: 'right', background: 'none', border: 'none', cursor: 'pointer' }}
          >
            ×
          </button>
        </div>
      )}

      <div style={styles.controls}>
        <div style={styles.searchSection}>
          <input
            type="text"
            placeholder="Search employees by name or nickname..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={styles.searchInput}
          />

          <div style={styles.viewToggle}>
            <button
              style={{
                ...styles.toggleButton,
                ...(viewMode === 'list' ? styles.toggleButtonActive : {})
              }}
              onClick={() => setViewMode('list')}
            >
              <Users size={16} />
              All Employees
            </button>
            <button
              style={{
                ...styles.toggleButton,
                ...(viewMode === 'crews' ? styles.toggleButtonActive : {})
              }}
              onClick={() => setViewMode('crews')}
            >
              <Users size={16} />
              By Crew
            </button>
          </div>

          <button style={styles.addButton} onClick={handleAddEmployee}>
            <UserPlus size={16} />
            Add Employee
          </button>
        </div>
      </div>

      {loading ? (
        <div style={styles.loading}>
          <p>Loading employees...</p>
        </div>
      ) : viewMode === 'list' ? (
        /* List View */
        <div style={styles.employeeList}>
          {filteredEmployees.length === 0 ? (
            <div style={styles.noData}>
              No employees found
            </div>
          ) : (
            filteredEmployees.map(employee => (
              <div key={employee.employee_id} style={styles.employeeCard}>
                <div style={styles.employeeHeader}>
                  <div style={styles.employeeInfo}>
                    <div style={styles.employeeName}>
                      {employee.first_name} {employee.last_name}
                    </div>
                    {employee.nickname && (
                      <div style={styles.employeeNickname}>
                        "{employee.nickname}"
                      </div>
                    )}
                    <div style={styles.employeeDetails}>
                      <div style={styles.detailRow}>
                        <strong>Role:</strong>{' '}
                        <span style={{
                          ...styles.roleTag,
                          ...(employee.role === 'admin' ? styles.adminRole :
                              (employee.role === 'foreman' || employee.role === 'asphalt_foreman') ? styles.foremanRole : styles.workerRole)
                        }}>
                          {employee.role === 'asphalt_foreman' ? 'asphalt foreman' : employee.role}
                        </span>
                      </div>
                      <div style={styles.detailRow}>
                        <strong>Email:</strong> {employee.email}
                      </div>
                      <div style={styles.detailRow}>
                        <strong>Phone:</strong> {employee.phone}
                      </div>
                      {employee.crew_foreman_id && (
                        <div style={styles.detailRow}>
                          <strong>Foreman:</strong> {employee.foreman_first_name} {employee.foreman_last_name}
                        </div>
                      )}
                    </div>
                  </div>

                  <div style={styles.employeeActions}>
                    <button
                      style={{ ...styles.actionButton, ...styles.editButton }}
                      onClick={() => handleEditEmployee(employee)}
                    >
                      <Edit size={14} />
                      Edit
                    </button>
                    <button
                      style={{ ...styles.actionButton, ...styles.pinButton }}
                      onClick={() => handleChangePinClick(employee)}
                    >
                      <Key size={14} />
                      PIN
                    </button>
                    <button
                      style={{ ...styles.actionButton, ...styles.deactivateButton }}
                      onClick={() => handleDeactivateEmployee(employee)}
                    >
                      <UserX size={14} />
                      Deactivate
                    </button>
                    <button
                      style={{ ...styles.actionButton, ...styles.deleteButton }}
                      onClick={() => handleDeleteEmployee(employee)}
                    >
                      <Trash2 size={14} />
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      ) : (
        /* Crew View */
        <div>
          {crewGroups().map((group, index) => {
            const isExpanded = expandedCrews.has(group.foreman?.employee_id || 0);
            return (
              <div key={group.foreman?.employee_id || 'unassigned'} style={styles.crewSection}>
                <div
                  style={styles.crewHeader}
                  onClick={() => toggleCrewExpansion(group.foreman?.employee_id || null)}
                >
                  <div style={styles.crewTitle}>
                    {isExpanded ? <ChevronDown size={20} /> : <ChevronRight size={20} />}
                    {group.foreman ? (
                      `${group.foreman.first_name} ${group.foreman.last_name}'s Crew`
                    ) : (
                      'Unassigned Employees'
                    )}
                    {group.foreman?.nickname && (
                      <span style={{ fontStyle: 'italic', color: '#6b7280' }}>
                        "{group.foreman.nickname}"
                      </span>
                    )}
                  </div>
                  <div style={styles.crewCount}>
                    {group.members.length} member{group.members.length !== 1 ? 's' : ''}
                  </div>
                </div>

                {isExpanded && (
                  <div style={styles.crewMembers}>
                    {group.foreman && (
                      <div style={{...styles.employeeCard, backgroundColor: '#f8fafc'}}>
                        <div style={styles.employeeHeader}>
                          <div style={styles.employeeInfo}>
                            <div style={styles.employeeName}>
                              {group.foreman.first_name} {group.foreman.last_name}
                              <span style={{
                                ...styles.roleTag,
                                ...styles.foremanRole,
                                marginLeft: '8px'
                              }}>
                                {group.foreman.role === 'asphalt_foreman' ? 'ASPHALT FOREMAN' : 'FOREMAN'}
                              </span>
                            </div>
                            {group.foreman.nickname && (
                              <div style={styles.employeeNickname}>
                                "{group.foreman.nickname}"
                              </div>
                            )}
                            <div style={styles.employeeDetails}>
                              <div style={styles.detailRow}>
                                <strong>Email:</strong> {group.foreman.email}
                              </div>
                              <div style={styles.detailRow}>
                                <strong>Phone:</strong> {group.foreman.phone}
                              </div>
                            </div>
                          </div>
                          <div style={styles.employeeActions}>
                            <button
                              style={{ ...styles.actionButton, ...styles.editButton }}
                              onClick={() => handleEditEmployee(group.foreman!)}
                            >
                              <Edit size={14} />
                              Edit
                            </button>
                            <button
                              style={{ ...styles.actionButton, ...styles.pinButton }}
                              onClick={() => handleChangePinClick(group.foreman!)}
                            >
                              <Key size={14} />
                              PIN
                            </button>
                          </div>
                        </div>
                      </div>
                    )}

                    {group.members.map(member => (
                      <div key={member.employee_id} style={styles.employeeCard}>
                        <div style={styles.employeeHeader}>
                          <div style={styles.employeeInfo}>
                            <div style={styles.employeeName}>
                              {member.first_name} {member.last_name}
                            </div>
                            {member.nickname && (
                              <div style={styles.employeeNickname}>
                                "{member.nickname}"
                              </div>
                            )}
                            <div style={styles.employeeDetails}>
                              <div style={styles.detailRow}>
                                <strong>Role:</strong>{' '}
                                <span style={{
                                  ...styles.roleTag,
                                  ...(member.role === 'admin' ? styles.adminRole : styles.workerRole)
                                }}>
                                  {member.role}
                                </span>
                              </div>
                              <div style={styles.detailRow}>
                                <strong>Email:</strong> {member.email}
                              </div>
                              <div style={styles.detailRow}>
                                <strong>Phone:</strong> {member.phone}
                              </div>
                            </div>
                          </div>
                          <div style={styles.employeeActions}>
                            <button
                              style={{ ...styles.actionButton, ...styles.editButton }}
                              onClick={() => handleEditEmployee(member)}
                            >
                              <Edit size={14} />
                              Edit
                            </button>
                            <button
                              style={{ ...styles.actionButton, ...styles.pinButton }}
                              onClick={() => handleChangePinClick(member)}
                            >
                              <Key size={14} />
                              PIN
                            </button>
                            <button
                              style={{ ...styles.actionButton, ...styles.deactivateButton }}
                              onClick={() => handleDeactivateEmployee(member)}
                            >
                              <UserX size={14} />
                              Deactivate
                            </button>
                            <button
                              style={{ ...styles.actionButton, ...styles.deleteButton }}
                              onClick={() => handleDeleteEmployee(member)}
                            >
                              <Trash2 size={14} />
                              Delete
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}

                    {group.members.length === 0 && (
                      <div style={styles.noData}>
                        No crew members
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Add/Edit Employee Modal */}
      {showAddModal && (
        <div
          style={styles.modal}
          onMouseDown={(e) => {
            // Only close if clicking directly on the modal overlay background
            if (e.target === e.currentTarget) {
              // Small delay to prevent accidental closes during text selection
              setTimeout(() => setShowAddModal(false), 10);
            }
          }}
        >
          <div style={styles.modalContent}>
            <div style={styles.modalHeader}>
              <h2 style={styles.modalTitle}>
                {editingEmployee ? 'Edit Employee' : 'Add New Employee'}
              </h2>
              <button style={styles.closeButton} onClick={() => setShowAddModal(false)}>
                <X size={20} />
              </button>
            </div>

            {error && (
              <div style={styles.error}>
                {error}
                <button
                  onClick={() => setError(null)}
                  style={{ float: 'right', background: 'none', border: 'none', cursor: 'pointer', color: '#dc2626' }}
                >
                  ×
                </button>
              </div>
            )}

            <div style={styles.formGroup}>
              <label style={styles.label}>First Name *</label>
              <input
                type="text"
                style={styles.input}
                value={formData.first_name}
                onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                placeholder="John"
              />
            </div>

            <div style={styles.formGroup}>
              <label style={styles.label}>Last Name *</label>
              <input
                type="text"
                style={styles.input}
                value={formData.last_name}
                onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                placeholder="Doe"
              />
            </div>

            <div style={styles.formGroup}>
              <label style={styles.label}>Nickname</label>
              <input
                type="text"
                style={styles.input}
                value={formData.nickname}
                onChange={(e) => setFormData({ ...formData, nickname: e.target.value })}
                placeholder="Johnny"
              />
            </div>

            <div style={styles.formGroup}>
              <label style={styles.label}>Email</label>
              <input
                type="email"
                style={styles.input}
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                placeholder="john.doe@example.com (optional)"
              />
            </div>

            <div style={styles.formGroup}>
              <label style={styles.label}>Phone *</label>
              <input
                type="tel"
                style={styles.input}
                value={formData.phone}
                onChange={(e) => handlePhoneChange(e.target.value)}
                placeholder="Enter phone number (auto-formats)"
                maxLength={12} // 10 digits + 2 dashes
              />
            </div>

            {!editingEmployee && (
              <div style={styles.formGroup}>
                <label style={styles.label}>PIN * (4 digits)</label>
                <input
                  type="password"
                  style={styles.input}
                  value={formData.pin}
                  onChange={(e) => setFormData({ ...formData, pin: e.target.value })}
                  placeholder="1234"
                  maxLength={4}
                />
              </div>
            )}

            <div style={styles.formGroup}>
              <label style={styles.label}>Role *</label>
              <CustomSelect
                value={formData.role}
                onChange={(value) => {
                  const newRole = value as any;
                  // Clear foreman assignment if role is not field_worker
                  if (newRole !== 'field_worker') {
                    setFormData({ ...formData, role: newRole, crew_foreman_id: '' });
                  } else {
                    setFormData({ ...formData, role: newRole });
                  }
                }}
                placeholder="Select Role"
                options={[
                  { value: 'field_worker', label: 'Field Worker' },
                  { value: 'foreman', label: 'Foreman' },
                  { value: 'asphalt_foreman', label: 'Asphalt Foreman' },
                  { value: 'truck_driver', label: 'CDL Truck Driver' },
                  { value: 'mechanic', label: 'Mechanic' },
                  ...(auth?.user?.is_super_admin ? [{ value: 'admin', label: 'Admin' }] : [])
                ]}
              />
            </div>

            {formData.role === 'field_worker' && (
              <div style={styles.formGroup}>
                <label style={styles.label}>Assign to Foreman</label>
                <CustomSelect
                  value={formData.crew_foreman_id}
                  onChange={(value) => setFormData({ ...formData, crew_foreman_id: value })}
                  placeholder="No Foreman (Unassigned)"
                  options={foremen.map(foreman => ({
                    value: foreman.employee_id,
                    label: `${foreman.first_name} ${foreman.last_name}${foreman.nickname ? ` "${foreman.nickname}"` : ''}`
                  }))}
                />
              </div>
            )}

            <div style={styles.modalActions}>
              <button style={styles.cancelButton} onClick={() => setShowAddModal(false)}>
                Cancel
              </button>
              <button style={styles.saveButton} onClick={handleSaveEmployee} disabled={loading}>
                <Save size={16} />
                {editingEmployee ? 'Update' : 'Create'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Change PIN Modal */}
      {showPinModal && pinEmployee && (
        <div
          style={styles.modal}
          onMouseDown={(e) => {
            // Only close if clicking directly on the modal overlay background
            if (e.target === e.currentTarget) {
              // Small delay to prevent accidental closes during text selection
              setTimeout(() => setShowPinModal(false), 10);
            }
          }}
        >
          <div style={styles.modalContent}>
            <div style={styles.modalHeader}>
              <h2 style={styles.modalTitle}>
                Change PIN for {pinEmployee.first_name} {pinEmployee.last_name}
              </h2>
              <button style={styles.closeButton} onClick={() => setShowPinModal(false)}>
                <X size={20} />
              </button>
            </div>

            {error && (
              <div style={styles.error}>
                {error}
                <button
                  onClick={() => setError(null)}
                  style={{ float: 'right', background: 'none', border: 'none', cursor: 'pointer', color: '#dc2626' }}
                >
                  ×
                </button>
              </div>
            )}

            <div style={styles.formGroup}>
              <label style={styles.label}>New PIN (4 digits)</label>
              <input
                type="password"
                style={styles.input}
                value={pinData.pin}
                onChange={(e) => setPinData({ pin: e.target.value })}
                placeholder="1234"
                maxLength={4}
                autoFocus
              />
            </div>

            <div style={styles.modalActions}>
              <button style={styles.cancelButton} onClick={() => setShowPinModal(false)}>
                Cancel
              </button>
              <button style={styles.saveButton} onClick={handleUpdatePin} disabled={loading || pinData.pin.length !== 4}>
                <Key size={16} />
                Update PIN
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};